import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import {
  turnoActivo, turnoAbrir, turnoCerrar, agentesDisponibles,
  turnoAgregarPersonal, turnoQuitarPersonal,
  destacamentosList, type TurnoActivo, type AgenteDisp, type Destacamento,
} from '../api/seguridad';

const ROL_TURNO: Record<string, string> = { jefe_guardia: 'Jefe de guardia', agente: 'Agente', radio_operador: 'Radio' };
const TIPOS = [['mañana', '🌅 Mañana'], ['tarde', '🌇 Tarde'], ['noche', '🌙 Noche'], ['24hs', '🕛 24 hs'], ['especial', '⭐ Especial']];

function hora(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso.replace(' ', 'T')).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function Guardia() {
  const user = useAuth((s) => s.user);
  const nav = useNavigate();

  const [destId, setDestId] = useState<number | null>(user?.destacamento_id ?? null);
  const [destacamentos, setDestacamentos] = useState<Destacamento[]>([]);
  const [turno, setTurno] = useState<TurnoActivo>(null);
  const [disp, setDisp] = useState<AgenteDisp[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tipo, setTipo] = useState('mañana');
  const [agregando, setAgregando] = useState(false);
  const [cerrandoModal, setCerrandoModal] = useState(false);
  const [novedades, setNovedades] = useState('');
  const [confirmTxt, setConfirmTxt] = useState('');

  useEffect(() => { if (!destId) destacamentosList().then(setDestacamentos).catch(() => {}); }, [destId]);

  const cargar = useCallback(async () => {
    if (!destId) return;
    try {
      const t = await turnoActivo(destId);
      setTurno(t);
      if (t) setDisp(await agentesDisponibles(destId));
      setErr(null);
    } catch (e: any) { setErr(e?.message || 'Error al cargar la guardia'); }
  }, [destId]);

  useEffect(() => { cargar(); }, [cargar]);

  async function abrir() {
    setBusy(true);
    try { await turnoAbrir(destId!, tipo); await cargar(); }
    catch (e: any) { setErr(e?.message || 'No se pudo abrir'); }
    finally { setBusy(false); }
  }
  async function cerrar() {
    setBusy(true);
    try {
      const r = await turnoCerrar(turno!.id, novedades.trim() || undefined);
      setCerrandoModal(false); setNovedades(''); setConfirmTxt('');
      if (r.releva) setErr(null);
      await cargar();
      if (r.releva) setOk(`Turno cerrado. Relevo automático al turno ${r.nuevo_turno_tipo ?? 'siguiente'}. Las alertas sin resolver pasaron al nuevo turno.`);
      else setOk('Turno cerrado. No hay turno planificado para ahora — quedó sin cobertura.');
    }
    catch (e: any) { setErr(e?.message || 'No se pudo cerrar'); }
    finally { setBusy(false); }
  }
  async function agregar(a: AgenteDisp, rol: string) {
    setBusy(true);
    try { await turnoAgregarPersonal(turno!.id, a.id, rol); setAgregando(false); await cargar(); }
    catch (e: any) { setErr(e?.message || 'No se pudo agregar'); }
    finally { setBusy(false); }
  }
  async function quitar(tpId: number) {
    setBusy(true);
    try { await turnoQuitarPersonal(tpId); await cargar(); }
    catch (e: any) { setErr(e?.message || 'Error'); }
    finally { setBusy(false); }
  }
  if (!user) return null;
  const yaEnTurno = new Set((turno?.personal ?? []).filter((x) => !x.egreso_en).map((x) => x.personal_id));
  const disponibles = disp.filter((a) => !yaEnTurno.has(a.id));

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 shrink-0">
        <button onClick={() => nav('/')} className="text-slate-500 hover:text-slate-900 text-xl leading-none">←</button>
        <div className="flex-1">
          <h1 className="text-slate-900 font-bold leading-tight">Guardia</h1>
          <p className="text-slate-500 text-xs leading-tight">{user.destacamento_nombre ?? destacamentos.find((d) => d.id === destId)?.nombre ?? 'Comisaría'}</p>
        </div>
      </header>

      {err && <div className="bg-red-50 border-b border-red-200 text-red-700 text-xs px-4 py-1.5">{err}</div>}
      {ok && <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-700 text-xs px-4 py-1.5">{ok}</div>}

      {!destId && (
        <div className="p-4 max-w-md">
          <label className="block text-sm font-semibold text-slate-700 mb-1">Elegí un destacamento</label>
          <select onChange={(e) => setDestId(Number(e.target.value) || null)} className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm">
            <option value="">— seleccioná —</option>
            {destacamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
        </div>
      )}

      <main className="flex-1 overflow-y-auto p-3 max-w-2xl w-full mx-auto space-y-3">
        {destId && !turno && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
            <p className="text-slate-500 text-sm mb-3">No hay un turno de guardia abierto.</p>
            <div className="flex flex-wrap gap-2 justify-center mb-3">
              {TIPOS.map(([v, l]) => (
                <button key={v} onClick={() => setTipo(v)}
                  className={`text-sm font-semibold rounded-lg px-3 py-2 border ${tipo === v ? 'bg-brand-500 text-white border-brand-500' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
              ))}
            </div>
            <button disabled={busy} onClick={abrir} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl px-6 py-2.5 text-sm font-semibold">
              Abrir turno
            </button>
          </div>
        )}

        {destId && turno && (
          <>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-900 font-bold">Turno activo · {turno.tipo}</p>
                  <p className="text-slate-500 text-xs">Abierto {hora(turno.fecha_inicio)} · {turno.personal_activo} en servicio</p>
                </div>
                <button disabled={busy} onClick={() => { setNovedades(''); setConfirmTxt(''); setCerrandoModal(true); }} className="border border-red-300 hover:bg-red-50 text-red-700 rounded-lg px-3 py-2 text-sm font-semibold">Cerrar turno</button>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-700 font-semibold text-sm">Personal de guardia</p>
                <button onClick={() => setAgregando((v) => !v)} className="text-brand-600 text-sm font-semibold">+ Agregar</button>
              </div>

              {agregando && (
                <div className="mb-3 border border-slate-200 rounded-lg p-2 bg-slate-50">
                  {disponibles.length === 0 && <p className="text-slate-400 text-xs py-2 text-center">No hay más personal del roster para agregar.</p>}
                  {disponibles.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0">
                      <span className="text-sm text-slate-700">
                        {a.apellido}, {a.nombre}
                        {a.cargo_nombre && <span className="text-slate-400"> · {a.cargo_nombre}</span>}
                        {a.admin_usuario_id ? ' 📲' : ' 🔕'}
                      </span>
                      <div className="flex gap-1">
                        <button disabled={busy} onClick={() => agregar(a, 'agente')} className="text-xs bg-brand-500 text-white rounded px-2 py-1">Agente</button>
                        <button disabled={busy} onClick={() => agregar(a, 'radio_operador')} className="text-xs bg-slate-600 text-white rounded px-2 py-1">Radio</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-1.5">
                {(turno.personal ?? []).filter((x) => !x.egreso_en).length === 0 && <p className="text-slate-400 text-sm text-center py-3">Nadie en servicio todavía.</p>}
                {(turno.personal ?? []).filter((x) => !x.egreso_en).map((per) => (
                  <div key={per.tp_id} className="flex items-center justify-between gap-2 py-1.5">
                    <div className="min-w-0">
                      <p className="text-slate-900 text-sm font-medium truncate">
                        {per.nombre} {per.admin_usuario_id ? '📲' : '🔕'}
                      </p>
                      <p className="text-slate-500 text-xs">
                        {ROL_TURNO[per.rol_turno] ?? per.rol_turno} · desde {hora(per.ingreso_en)}
                        {!per.admin_usuario_id && <span className="text-amber-600"> · sin cuenta de app</span>}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button disabled={busy} onClick={() => quitar(per.tp_id)} className="text-xs border border-red-300 rounded px-2 py-1 text-red-600 hover:bg-red-50">Sacar</button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-slate-400 text-[11px] mt-2">📲 = recibe alertas (tiene cuenta de app) · 🔕 = sin cuenta de app</p>
            </div>
          </>
        )}
      </main>

      {cerrandoModal && turno && (
        <div className="fixed inset-0 z-[1300] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setCerrandoModal(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="font-bold text-slate-900">Cerrar turno de guardia</h2>
              <p className="text-xs text-slate-500">Se dará egreso a todo el personal en servicio.</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[12px] text-amber-800">
                Las alertas sin resolver pasarán automáticamente al próximo turno. Si no hay un turno planificado para este horario, la comisaría quedará <b>sin cobertura</b> hasta el siguiente.
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Novedades del cierre (acta de relevo)</label>
                <textarea value={novedades} onChange={(e) => setNovedades(e.target.value)} rows={3}
                  placeholder="Detalle lo ocurrido en el turno, casos abiertos, observaciones…"
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Para confirmar, escribí <b className="text-red-600">CERRAR</b>
                </label>
                <input value={confirmTxt} onChange={(e) => setConfirmTxt(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-sm" placeholder="CERRAR" />
              </div>
            </div>
            <div className="flex gap-2 px-4 py-3 border-t border-slate-200">
              <button onClick={() => setCerrandoModal(false)} className="flex-1 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl py-2.5 text-sm font-semibold">Cancelar</button>
              <button disabled={busy || confirmTxt.trim().toUpperCase() !== 'CERRAR'} onClick={cerrar}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white rounded-xl py-2.5 text-sm font-semibold">
                {busy ? 'Cerrando…' : 'Cerrar turno'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
